import React, { useState } from "react";

export default function TicTacToe() {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [difficulty, setDifficulty] = useState("easy");
  const [mode, setMode] = useState("ai"); // "ai" or "player"

  const result = calculateWinner(board);
  const winner = result?.winner;
  const winningLine = result?.line || [];

  const handleClick = (i) => {
    if (board[i] || result) return;

    const newBoard = [...board];

    // 👥 PLAYER VS PLAYER
    if (mode === "player") {
      newBoard[i] = isPlayerTurn ? "X" : "O";
      setBoard(newBoard);
      setIsPlayerTurn(!isPlayerTurn);
      return;
    }

    // 🤖 VS AI
    if (!isPlayerTurn) return;

    newBoard[i] = "X";
    setBoard(newBoard);
    setIsPlayerTurn(false);

    setTimeout(() => {
      const move =
        difficulty === "easy"
          ? easyMove(newBoard)
          : bestMove(newBoard);

      if (move !== null) {
        newBoard[move] = "O";
        setBoard([...newBoard]);
      }

      setIsPlayerTurn(true);
    }, 400);
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setIsPlayerTurn(true);
  };

  return (
    <div className="game-container">
      <h2>🎮 Tic Tac Toe</h2>

      {/* MODE SWITCH */}
      <div className="mode-select">
        <button
          className={mode === "player" ? "active" : ""}
          onClick={() => {
            setMode("player");
            resetGame();
          }}
        >
          👥 VS Player
        </button>

        <button
          className={mode === "ai" ? "active" : ""}
          onClick={() => {
            setMode("ai");
            resetGame();
          }}
        >
          🤖 VS AI
        </button>
      </div>

      {/* DIFFICULTY (ONLY AI) */}
      {mode === "ai" && (
        <div className="difficulty">
          <button
            className={difficulty === "easy" ? "active" : ""}
            onClick={() => setDifficulty("easy")}
          >
            Easy
          </button>
          <button
            className={difficulty === "hard" ? "active" : ""}
            onClick={() => setDifficulty("hard")}
          >
            Hard
          </button>
        </div>
      )}

      {/* STATUS */}
      <div className="status">
        {winner
          ? winner === "Draw"
            ? "It's a Draw!"
            : `Winner: ${winner}`
          : mode === "player"
          ? `Turn: ${isPlayerTurn ? "X" : "O"}`
          : isPlayerTurn
          ? "Your Turn (X)"
          : "Computer thinking..."}
      </div>

      {/* BOARD */}
      <div className="board">
        {board.map((cell, i) => (
          <div
            key={i}
            className={`cell ${winningLine.includes(i) ? "winning" : ""}`}
            onClick={() => handleClick(i)}
          >
            {cell === "X" && (
              <svg viewBox="0 0 100 100" className="mark">
                <line x1="20" y1="20" x2="80" y2="80" className="draw-x" />
                <line x1="80" y1="20" x2="20" y2="80" className="draw-x second" />
              </svg>
            )}

            {cell === "O" && (
              <svg viewBox="0 0 100 100" className="mark">
                <circle cx="50" cy="50" r="30" className="draw-o" />
              </svg>
            )}
          </div>
        ))}
      </div>

      {/* RESTART */}
      <button className="restart-btn" onClick={resetGame}>
        Restart Game
      </button>
    </div>
  );
}

/* ---------- WINNER ---------- */
function calculateWinner(board) {
  const lines = [
    [0,1,2],[3,4,5],[6,7,8],
    [0,3,6],[1,4,7],[2,5,8],
    [0,4,8],[2,4,6],
  ];

  for (let [a, b, c] of lines) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a], line: [a, b, c] };
    }
  }

  if (!board.includes(null)) return { winner: "Draw", line: [] };
  return null;
}

/* ---------- EASY AI ---------- */
function easyMove(board) {
  const empty = board
    .map((v, i) => (v === null ? i : null))
    .filter(v => v !== null);

  if (Math.random() < 0.7) {
    return empty[Math.floor(Math.random() * empty.length)];
  }

  return bestMove(board);
}

/* ---------- HARD AI ---------- */
function bestMove(board) {
  let bestScore = -Infinity;
  let move = null;

  for (let i = 0; i < 9; i++) {
    if (board[i] === null) {
      board[i] = "O";
      let score = minimax(board, 0, false);
      board[i] = null;

      if (score > bestScore) {
        bestScore = score;
        move = i;
      }
    }
  }

  return move;
}

function minimax(board, depth, isMaximizing) {
  const result = calculateWinner(board);

  if (result !== null) {
    if (result.winner === "O") return 10 - depth;
    if (result.winner === "X") return depth - 10;
    if (result.winner === "Draw") return 0;
  }

  if (isMaximizing) {
    let bestScore = -Infinity;

    for (let i = 0; i < 9; i++) {
      if (board[i] === null) {
        board[i] = "O";
        let score = minimax(board, depth + 1, false);
        board[i] = null;
        bestScore = Math.max(score, bestScore);
      }
    }

    return bestScore;
  } else {
    let bestScore = Infinity;

    for (let i = 0; i < 9; i++) {
      if (board[i] === null) {
        board[i] = "X";
        let score = minimax(board, depth + 1, true);
        board[i] = null;
        bestScore = Math.min(score, bestScore);
      }
    }

    return bestScore;
  }
}